/**
 * useLessonGenerationEvents
 *
 * Lesson-specific wrapper around the shared useWorkflowGenerationEvents hook.
 * Provides the same interface as before while delegating all transport,
 * polling fallback, and failure detection to the centralized hook.
 */

import {
  useWorkflowGenerationEvents,
  clearWorkflowHistoricalEventsCache,
} from '@/shared/hooks/useWorkflowGenerationEvents';
import type { WorkflowProgressEvent } from '@/core/events/workflowProgressApi';

export type { WorkflowProgressEvent as ProgressEvent } from '@/core/events/workflowProgressApi';

/** Clear all cached job state for lesson generation (e.g. on logout) */
export const clearLessonHistoricalEventsCache = (): void => {
  clearWorkflowHistoricalEventsCache('lesson_generation');
  clearWorkflowHistoricalEventsCache('lesson_content_generation');
};

// ── Hook ───────────────────────────────────────────────────────────

interface UseLessonGenerationEventsOptions {
  /** Active generation job ID (null when not generating) */
  activeJobId: string | null;
  /** Whether generation mode is active */
  isGenerating: boolean;
  /** Number of already-completed steps (used to skip redundant history fetches) */
  completedStepCount: number;
  /** Callback invoked for each progress event (live or historical replay) */
  onEvent: (event: WorkflowProgressEvent) => void;
  /** Callback when historical events contain a failure */
  onHistoricalFailure: (message: string) => void;
}

/**
 * Subscribes to Pusher real-time events for the active lesson generation
 * job and fetches historical events on mount.
 */
export function useLessonGenerationEvents({
  activeJobId,
  isGenerating,
  completedStepCount,
  onEvent,
  onHistoricalFailure,
}: UseLessonGenerationEventsOptions): void {
  useWorkflowGenerationEvents({
    workflowType: 'lesson_generation',
    activeJobId,
    isGenerating,
    completedStepCount,
    onEvent,
    onFailure: onHistoricalFailure,
  });
}
